/**
 * Agent Blueprints — Admin panel (H3.1: product-truth UX).
 *
 * Read-only listing + safe edit + guarded publish for agent_blueprints.
 *
 * Important UX rules (matched by tests in test_admin_blueprints.py
 * and test_h3_1_admin_truth_cleanup.py):
 *   • Banners reflect the CURRENT runtime per client. For Elido
 *     blueprint is live, so the banner says so; for legacy clients the
 *     banner explains they are still on handymen-runtime.
 *   • Publish button is DISABLED when flow_type or company_name are
 *     empty. Server enforces this too; UI just gives early feedback.
 *   • Edit form persists via PUT and recomputes uncertain_or_missing_info.
 *   • Approved Actions section copy adapts to active runtime — for
 *     blueprint-active clients it says actions are live; for legacy
 *     it says "this is preview only".
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Loader2, ChevronRight, ArrowLeft, ShieldAlert, ShieldCheck,
  CheckCircle2, AlertTriangle, Save, Send, FileText, ListTree,
  Archive, Info, ExternalLink, GitCompareArrows, MousePointerClick,
  Equal, ArrowRight, MinusCircle, PlusCircle, PlayCircle, Zap, ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";
import RuntimeStatusBanner, { useRuntimeStatus } from "./RuntimeStatusBanner";
import { BlueprintEvidencePanel } from "./BlueprintEvidencePanel";
import { CatalogPanel } from "./CatalogPanel";

// H3.1b — Swedish labels for Runtime jämförelse side-effects map.
const SIDE_EFFECT_LABELS = {
  wrote_to_handymen: "Skrev till handymen",
  leads_created: "Leads skapade",
  emails_sent: "Mejl skickade",
  llm_calls: "LLM-anrop",
};

// ─── helpers ────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  review_required: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-blue-100 text-blue-800 border-blue-300",
  published: "bg-emerald-100 text-emerald-800 border-emerald-300",
  archived: "bg-zinc-200 text-zinc-600 border-zinc-300",
};

const STATUS_LABELS = {
  draft: "Utkast",
  review_required: "Granskning krävs",
  approved: "Godkänd",
  published: "Publicerad",
  archived: "Arkiverad",
};

const FIELD_LABELS = {
  company_name: "Företagsnamn",
  industry: "Bransch",
  flow_type: "Flow-typ",
  assistant_role: "Assistentens roll",
  short_description: "Kort beskrivning",
  target_customers: "Målgrupp",
  service_areas: "Service-områden",
  services: "Tjänster",
  products_or_categories: "Produkter / kategorier",
  "contact.phone": "Kontakt: telefon",
  "contact.email": "Kontakt: e-post",
  "contact.address": "Kontakt: adress",
  "contact.contact_page": "Kontaktsida",
  opening_hours: "Öppettider",
  important_links: "Viktiga länkar",
  self_service_links: "Self-service-länkar",
  qualification_fields: "Kvalifieringsfält",
  "rules.do_not_guess": "Regler: gissa inte",
  faq: "FAQ",
  page_context_rules: "Sidkontext-regler",
};

const StatusBadge = ({ status }) => (
  <span
    data-testid={`blueprint-status-${status}`}
    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md border ${
      STATUS_COLORS[status] || "bg-gray-100 text-gray-700 border-gray-300"
    }`}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

// ─── List view ──────────────────────────────────────────────────────────

function BlueprintsList({ blueprints, onSelect, runtimeNotice }) {
  if (blueprints.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center">
        <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
        <h3 className="text-sm font-semibold text-gray-700">Inga blueprints ännu</h3>
        <p className="text-xs text-gray-500 mt-1">
          Kör migrations-skriptet eller skapa via onboarding för att se
          blueprints här.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="blueprints-list">
      {/* H3.2.1 → H3.3 iter2 — Specialistvy-banner. Kraftigare copy:
          Agent Blueprints är maskinrummet (JSON-motorn). Normal produktarbete
          sker i Kund 360. */}
      <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex items-start gap-2" data-testid="blueprints-specialist-banner">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold uppercase tracking-wide mb-0.5">Avancerad teknisk vy / maskinrum</div>
          <span>
            Agent Blueprints är den råa JSON-motorn för AI-agentens kunskap och regler. För normalt produktarbete — leads, knappar, utseende, runtime, monitor — använd <strong>Kund 360</strong> via <em>Kunder</em>-menyn.
          </span>
        </div>
      </div>
      {blueprints.map((bp) => (
        <div
          key={bp.client_id}
          data-testid={`blueprint-row-${bp.client_id}`}
          className="w-full flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4 hover:border-emerald-400 hover:shadow-sm transition"
        >
          <button
            type="button"
            onClick={() => onSelect(bp.client_id)}
            className="flex-1 min-w-0 text-left"
            data-testid={`blueprint-row-open-${bp.client_id}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-900 truncate">
                {bp.company_name || bp.client_id}
              </span>
              <StatusBadge status={bp.status} />
              <span className="text-xs text-gray-400">v{bp.version}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-1.5">
              <span className="text-xs text-gray-500 font-mono">{bp.client_id}</span>
              <span className="text-xs text-gray-300">·</span>
              <span className="text-xs text-gray-500">
                Komplett: {bp.completeness_percent}%
              </span>
              {bp.publish_blockers?.length > 0 && (
                <>
                  <span className="text-xs text-gray-300">·</span>
                  <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Blockerar publicering: {bp.publish_blockers.join(", ")}
                  </span>
                </>
              )}
            </div>
          </button>
          {/* H3.2.1 — direkt-länk till Kund 360 */}
          <Link
            to={`/admin/clients/${encodeURIComponent(bp.client_id)}`}
            className="inline-flex items-center gap-1 text-xs font-medium text-[#255E69] hover:underline whitespace-nowrap shrink-0"
            data-testid={`blueprint-row-kund360-${bp.client_id}`}
            onClick={(e) => e.stopPropagation()}
          >
            Öppna Kund 360
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ))}
    </div>
  );
}

// ─── Detail view ────────────────────────────────────────────────────────

function FieldGrid({ entries }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {entries.map((e) => (
        <div
          key={e.key}
          className={`text-xs rounded-md px-3 py-2 border ${
            e.kind === "filled"
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-amber-50 border-amber-200 text-amber-900"
          }`}
          data-testid={`field-${e.kind}-${e.key.replace(/\./g, "-")}`}
        >
          <div className="flex items-center gap-1.5">
            {e.kind === "filled" ? (
              <CheckCircle2 className="w-3.5 h-3.5" />
            ) : (
              <AlertTriangle className="w-3.5 h-3.5" />
            )}
            <span className="font-medium">
              {FIELD_LABELS[e.key] || e.key}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApprovedActionsList({ clientId, getAuthHeaders }) {
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [note, setNote] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions`,
          { headers: getAuthHeaders(), credentials: "include" },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (cancelled) return;
        setActions(data.actions || []);
        setNote(data.note || "");
      } catch {
        if (!cancelled) toast.error("Kunde inte ladda actions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, getAuthHeaders]);

  if (loading) {
    return (
      <div className="text-xs text-gray-500 flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laddar actions…
      </div>
    );
  }
  if (actions.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic">
        Inga client-approved actions ännu. Admin lägger till per klient
        via Approved Actions-fliken.
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <p className="text-xs text-gray-500">{note}</p>
      {actions.map((a) => (
        <div
          key={a.action_id}
          className="text-xs rounded-md border border-gray-200 bg-gray-50 px-3 py-2 flex items-center justify-between gap-2"
          data-testid={`client-action-${a.action_id}`}
        >
          <span className="font-mono">{a.action_id}</span>
          <span className="text-gray-500">{a.status}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Checkpoint F: Diff tab (handymen vs blueprint) ─────────────────────

const DIFF_STATUS_STYLES = {
  same:           { tone: "bg-emerald-50 border-emerald-200 text-emerald-900", icon: Equal,         label: "Lika" },
  different:      { tone: "bg-rose-50    border-rose-300    text-rose-900",    icon: ArrowRight,    label: "Skiljer sig" },
  only_blueprint: { tone: "bg-blue-50    border-blue-200    text-blue-900",    icon: PlusCircle,    label: "Endast i mål (nytt)" },
  only_handyman:  { tone: "bg-amber-50   border-amber-300   text-amber-900",   icon: MinusCircle,   label: "Endast i live handymen (försvinner vid cutover)" },
  both_empty:     { tone: "bg-gray-50    border-gray-200    text-gray-500",    icon: Info,          label: "Båda tomma" },
};

// Checkpoint G: destination badges per row. Tells admin WHERE the
// field should land — agent_blueprint, widget_presentation, or
// needs manual review (legacy pricing).
const DESTINATION_BADGES = {
  agent_blueprint:     { label: "→ Agent Blueprint",    color: "bg-purple-100 text-purple-800 border-purple-300" },
  widget_presentation: { label: "→ Widget Presentation", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  pricing_analysis:    { label: "→ Behöver granskning",  color: "bg-orange-100 text-orange-800 border-orange-300" },
  none:                { label: "Försvinner",            color: "bg-gray-200 text-gray-700 border-gray-300" },
};

function _formatDiffValue(v) {
  if (v === null || v === undefined) return <span className="text-gray-400 italic">(saknas)</span>;
  if (typeof v === "string") {
    if (!v.trim()) return <span className="text-gray-400 italic">(tom)</span>;
    return <span>{v}</span>;
  }
  if (typeof v === "number" || typeof v === "boolean") {
    return <span>{String(v)}</span>;
  }
  return (
    <pre className="text-xs whitespace-pre-wrap break-all font-mono leading-tight">
      {JSON.stringify(v, null, 2)}
    </pre>
  );
}

function DiffTab({ clientId, getAuthHeaders }) {
  const { status: runtimeStatus } = useRuntimeStatus(clientId);
  const isBlueprintActive = runtimeStatus?.active_runtime === "blueprint";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("changed_or_critical");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/diff`,
          { headers: getAuthHeaders(), credentials: "include" },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const payload = await r.json();
        if (cancelled) return;
        setData(payload);
      } catch {
        if (!cancelled) toast.error("Kunde inte ladda diff");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, getAuthHeaders]);

  if (loading || !data) {
    return (
      <div className="flex items-center gap-2 text-gray-600 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Laddar diff…
      </div>
    );
  }

  const diff = data.diff;
  const fields = diff.fields;
  const visible = fields.filter((f) => {
    if (filter === "all") return true;
    if (filter === "critical") return f.is_critical && f.status !== "same" && f.status !== "both_empty";
    return f.status !== "same" && f.status !== "both_empty";
  });

  return (
    <div className="space-y-4" data-testid="blueprint-diff-tab">
      {/* Runtime banner — dynamic per client */}
      <RuntimeStatusBanner
        clientId={clientId}
        testid="diff-runtime-banner"
        contextHint="Diff visar skillnaden mellan legacy handymen och aktiv blueprint-runtime. Legacy finns kvar som rollback."
      />

      {/* Summary counts */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <GitCompareArrows className="w-4 h-4" />
            Diff handymen → blueprint
          </h2>
          <div className="flex items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1 text-emerald-700"><Equal className="w-3 h-3" />Lika: {diff.summary.same}</span>
            <span className="inline-flex items-center gap-1 text-rose-700"><ArrowRight className="w-3 h-3" />Skiljer: {diff.summary.different}</span>
            <span className="inline-flex items-center gap-1 text-blue-700"><PlusCircle className="w-3 h-3" />Nya i mål: {diff.summary.only_blueprint}</span>
            <span className="inline-flex items-center gap-1 text-amber-700"><MinusCircle className="w-3 h-3" />Försvinner: {diff.summary.only_handyman}</span>
            <span className="inline-flex items-center gap-1 text-gray-500"><Info className="w-3 h-3" />Båda tomma: {diff.summary.both_empty}</span>
          </div>
        </div>

        {/* Destination summary (Checkpoint G) */}
        {diff.destination_summary && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]" data-testid="diff-destination-summary">
            <span className="text-gray-500">Dataägarskap:</span>
            {Object.entries(diff.destination_summary).map(([dest, count]) => {
              const badge = DESTINATION_BADGES[dest] || DESTINATION_BADGES.none;
              return (
                <span key={dest} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${badge.color}`}>
                  {badge.label}: {count}
                </span>
              );
            })}
          </div>
        )}
        {diff.critical_diffs.length > 0 && (
          <p className="mt-3 text-xs text-rose-800 flex items-start gap-1.5" data-testid="diff-critical-list">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              <strong>
                {isBlueprintActive
                  ? "Kritiska skillnader mellan legacy och aktiv blueprint-runtime:"
                  : "Kritiska skillnader som skulle påverka runtime om blueprint aktiveras:"}
              </strong>{" "}
              {diff.critical_diffs.join(", ")}
            </span>
          </p>
        )}
        {!diff.handyman_present && (
          <p className="mt-2 text-xs text-gray-500 italic">
            Ingen matchande handymen-dokument hittades — alla rader visas som &quot;Endast i blueprint&quot; eller &quot;Båda tomma&quot;.
          </p>
        )}

        <div className="mt-4 flex items-center gap-2 text-xs">
          <span className="text-gray-500">Visa:</span>
          {[
            { id: "changed_or_critical", label: "Bara ändringar" },
            { id: "critical",            label: "Bara kritiska" },
            { id: "all",                 label: "Alla fält" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFilter(opt.id)}
              data-testid={`diff-filter-${opt.id}`}
              className={`px-2 py-1 rounded border ${
                filter === opt.id
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Side-by-side rows */}
      <div className="space-y-2">
        {visible.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-xs text-gray-500">
            Inga ändringar med aktuellt filter.
          </div>
        ) : visible.map((row) => {
          const style = DIFF_STATUS_STYLES[row.status] || DIFF_STATUS_STYLES.both_empty;
          const Icon = style.icon;
          const destBadge = DESTINATION_BADGES[row.destination] || DESTINATION_BADGES.agent_blueprint;
          return (
            <div
              key={row.label}
              data-testid={`diff-row-${row.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className={`rounded-lg border ${style.tone} p-3 space-y-2`}
            >
              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2 font-semibold">
                  <Icon className="w-3.5 h-3.5" />
                  <span>{row.label}</span>
                  {row.is_critical && (
                    <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-rose-100 text-rose-700 border border-rose-300">
                      kritisk
                    </span>
                  )}
                  <span
                    data-testid={`diff-row-destination-${row.destination}`}
                    className={`px-1.5 py-0.5 text-[10px] rounded border ${destBadge.color}`}
                    title={`Destination: ${row.destination}`}
                  >
                    {destBadge.label}
                  </span>
                </div>
                <span className="text-[11px]">{style.label}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="bg-white/70 rounded border border-gray-200 p-2">
                  <div className="text-[10px] uppercase text-gray-500 mb-1">
                    Live handymen
                    {row.handyman_source_path && (
                      <span className="font-mono text-gray-400">
                        {" "}({row.handyman_source_path})
                      </span>
                    )}
                  </div>
                  {_formatDiffValue(row.handyman_value)}
                </div>
                <div className="bg-white/70 rounded border border-gray-200 p-2">
                  <div className="text-[10px] uppercase text-gray-500 mb-1">
                    Mål (blueprint)
                    {row.target_path && (
                      <span className="font-mono text-gray-400">
                        {" "}({row.target_path})
                      </span>
                    )}
                  </div>
                  {_formatDiffValue(row.blueprint_value)}
                </div>
              </div>

              {/* Checkpoint G: pricing breakdown — surfaces 3 buckets */}
              {row.pricing_breakdown && row.pricing_breakdown.summary.is_present && (
                <div className="mt-2 rounded border border-orange-200 bg-orange-50 p-2 text-[11px] space-y-2" data-testid={`diff-row-pricing-breakdown`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-orange-900">Pricing-analys:</span>
                    <span className="text-orange-800">Blueprint-safe: {row.pricing_breakdown.summary.blueprint_safe}</span>
                    <span className="text-orange-800">Legacy/fallback: {row.pricing_breakdown.summary.legacy_fallback}</span>
                    <span className="text-orange-800">Tot: {row.pricing_breakdown.summary.total_entries}</span>
                    <span className="text-orange-700 italic">verdict: {row.pricing_breakdown.destination}</span>
                  </div>
                  {row.pricing_breakdown.blueprint_safe.length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-emerald-800">Blueprint-safe ({row.pricing_breakdown.blueprint_safe.length})</summary>
                      <ul className="ml-4 list-disc text-emerald-900">
                        {row.pricing_breakdown.blueprint_safe.map((e, i) => (
                          <li key={i}><strong>{e.label}:</strong> <span className="font-mono">{JSON.stringify(e.value)}</span> <span className="italic text-emerald-700">— {e.reason}</span></li>
                        ))}
                      </ul>
                    </details>
                  )}
                  {row.pricing_breakdown.legacy_fallback.length > 0 && (
                    <details>
                      <summary className="cursor-pointer text-amber-800">Legacy/fallback ({row.pricing_breakdown.legacy_fallback.length})</summary>
                      <ul className="ml-4 list-disc text-amber-900">
                        {row.pricing_breakdown.legacy_fallback.map((e, i) => (
                          <li key={i}><strong>{e.label}:</strong> <span className="font-mono">{JSON.stringify(e.value)}</span> <span className="italic text-amber-700">— {e.reason}</span></li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Checkpoint F: Actions preview tab ──────────────────────────────────

const URL_STATUS_STYLES = {
  ok:                  { tone: "bg-emerald-50 border-emerald-200 text-emerald-900", label: "URL OK" },
  missing:             { tone: "bg-amber-50   border-amber-300   text-amber-900",   label: "URL saknas" },
  unsafe:              { tone: "bg-rose-50    border-rose-300    text-rose-900",    label: "Osäker URL" },
  unresolved_template: { tone: "bg-rose-50    border-rose-300    text-rose-900",    label: "Ouppfyllda placeholders" },
};

const RENDER_AS_STYLES = {
  primary_button:   "bg-blue-600 text-white",
  secondary_button: "bg-white text-gray-900 border border-gray-300",
  link_card:        "bg-gray-50 text-gray-900 border border-gray-200",
};

function ActionsPreviewTab({ clientId, getAuthHeaders }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions-preview`,
          { headers: getAuthHeaders(), credentials: "include" },
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const payload = await r.json();
        if (cancelled) return;
        setData(payload);
      } catch {
        if (!cancelled) toast.error("Kunde inte ladda actions preview");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, getAuthHeaders]);

  if (loading || !data) {
    return (
      <div className="flex items-center gap-2 text-gray-600 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Laddar actions preview…
      </div>
    );
  }
  const summary = data.preview.summary;
  const actions = data.preview.actions;

  return (
    <div className="space-y-4" data-testid="blueprint-actions-preview-tab">
      <RuntimeStatusBanner
        clientId={clientId}
        testid="actions-preview-runtime-banner"
        contextHint="Endast kundgodkända actions med status approved kan renderas. Listan nedan är vad blueprint-runtime ser."
      />

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <MousePointerClick className="w-4 h-4" />
          Approved Actions
        </h2>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
            <div className="text-[10px] uppercase text-emerald-700">Runtime-eligible</div>
            <div className="font-semibold text-emerald-900">{summary.runtime_eligible}</div>
          </div>
          <div className="rounded border border-gray-200 bg-gray-50 p-2">
            <div className="text-[10px] uppercase text-gray-500">Totalt</div>
            <div className="font-semibold text-gray-900">{summary.total}</div>
          </div>
          <div className="rounded border border-rose-200 bg-rose-50 p-2">
            <div className="text-[10px] uppercase text-rose-700">Osäkra URL</div>
            <div className="font-semibold text-rose-900">{summary.blocked_unsafe}</div>
          </div>
          <div className="rounded border border-amber-200 bg-amber-50 p-2">
            <div className="text-[10px] uppercase text-amber-700">Saknar URL</div>
            <div className="font-semibold text-amber-900">{summary.blocked_missing_url}</div>
          </div>
          <div className="rounded border border-rose-200 bg-rose-50 p-2">
            <div className="text-[10px] uppercase text-rose-700">Placeholders kvar</div>
            <div className="font-semibold text-rose-900">{summary.blocked_template_placeholder}</div>
          </div>
        </div>
      </div>

      {actions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-xs text-gray-500">
          Inga client_approved_actions definierade för denna klient ännu.
        </div>
      ) : (
        <div className="space-y-3">
          {actions.map((a) => {
            const urlStyle = URL_STATUS_STYLES[a.url_status] || URL_STATUS_STYLES.missing;
            const btnStyle = RENDER_AS_STYLES[a.render_as] || RENDER_AS_STYLES.secondary_button;
            return (
              <div
                key={a.action_id}
                data-testid={`actions-preview-row-${a.action_id}`}
                className={`rounded-lg border p-3 ${urlStyle.tone} space-y-2`}
              >
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-mono">{a.action_id}</span>
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-white/70 border border-current/20">
                      {a.status}
                    </span>
                    <span className="px-1.5 py-0.5 rounded bg-white/70 border border-current/20">
                      {urlStyle.label}
                    </span>
                    {a.runtime_eligible ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" /> runtime-eligible
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700">
                        <AlertTriangle className="w-3 h-3" /> ej runtime-eligible
                      </span>
                    )}
                  </div>
                </div>
                <div className="rounded bg-white/70 border border-gray-200 p-3 flex items-center gap-3">
                  <button
                    type="button"
                    disabled
                    className={`px-3 py-1.5 text-xs font-medium rounded-md cursor-not-allowed ${btnStyle}`}
                  >
                    {a.label || "(label saknas)"}
                  </button>
                  {a.url && (
                    <span className="flex items-center gap-1.5 text-[11px] text-gray-500 truncate" title={a.url}>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      <span className="text-gray-400">Destination:</span>
                      <span className="font-mono truncate">{a.url}</span>
                    </span>
                  )}
                </div>
                {a.warnings.length > 0 && (
                  <ul className="text-[11px] list-disc list-inside">
                    {a.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}
                {a.evidence && (
                  <p className="text-[10px] text-gray-500 italic">Källa: {a.evidence}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Checkpoint H0: Runtime preview tab (dry-run, no side effects) ──────

function RuntimePreviewTab({ clientId, getAuthHeaders }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [testMessage, setTestMessage] = useState("Kan ni laga mitt kylskåp?");
  const [savingFlag, setSavingFlag] = useState(false);

  const load = useCallback(async (msg) => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/runtime-preview`
        + (msg ? `?test_message=${encodeURIComponent(msg)}` : "");
      const r = await fetch(url, {
        headers: getAuthHeaders(), credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const payload = await r.json();
      setData(payload);
    } catch {
      toast.error("Kunde inte ladda preview");
    } finally {
      setLoading(false);
    }
  }, [clientId, getAuthHeaders]);

  // Load once on mount with initial test message. Subsequent loads
  // happen only when the admin clicks "Kör test" (avoids running on
  // every keystroke).
  useEffect(() => { load("Kan ni laga mitt kylskåp?"); }, [load]);

  const flipFlag = async (newSource) => {
    if (!window.confirm(
      `Sätt runtime_source=${newSource}? ` +
      "För allowlistade klienter (t.ex. elido_official) växlar detta " +
      "blueprint-runtime av/på direkt. För övriga är det förberedande."
    )) return;
    setSavingFlag(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/runtime-source`,
        { method: "PUT",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({ source: newSource }) }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(`runtime_source = ${newSource}`);
      await load(testMessage);
    } catch (e) {
      toast.error(`Kunde inte spara: ${e.message || e}`);
    } finally {
      setSavingFlag(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center gap-2 text-gray-600 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Laddar runtime preview…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="blueprint-runtime-preview-tab">
      <RuntimeStatusBanner
        clientId={clientId}
        testid="runtime-preview-runtime-banner"
        contextHint="Här jämför du legacy handymen mot blueprint side-by-side och kontrollerar fallback/rollback-underlag."
      />

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Runtime source flag
          </h2>
          <div className="flex items-center gap-2">
            {["handymen", "blueprint"].map((s) => {
              const active = data.runtime_source_flag === s;
              return (
                <button
                  key={s}
                  type="button"
                  disabled={savingFlag || active}
                  onClick={() => flipFlag(s)}
                  data-testid={`runtime-source-${s}`}
                  className={`px-3 py-1 text-xs font-medium rounded-md border ${
                    active
                      ? "bg-emerald-600 text-white border-emerald-700"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div className={`rounded-md p-3 border ${
            data.would_use_blueprint_runtime
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-amber-50 border-amber-200 text-amber-900"}`}>
            <div className="text-[10px] uppercase opacity-60">Skulle använda blueprint?</div>
            <div className="font-semibold mt-0.5">
              {data.would_use_blueprint_runtime ? "JA" : "NEJ"}
            </div>
            <div className="mt-1 italic opacity-80">{data.would_use_blueprint_runtime_reason}</div>
          </div>
          <div className="rounded-md p-3 border bg-gray-50 border-gray-200 text-gray-800">
            <div className="text-[10px] uppercase opacity-60">Publishability</div>
            <div className="font-semibold mt-0.5">
              {data.publishability.ready_to_publish ? "Klar att publicera" : "Blockerad"}
            </div>
            <div className="mt-1 opacity-80">
              status: {data.publishability.status}
              {data.publishability.blockers.length > 0 && (
                <span className="text-rose-700"> · blockers: {data.publishability.blockers.join(", ")}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid="runtime-preview-side-effects">
        <h3 className="text-xs font-semibold text-gray-900 mb-1.5">Sidoeffekter (alla ska vara 0/false)</h3>
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          {Object.entries(data.side_effects).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-emerald-50 border-emerald-200 text-emerald-800">
              {(SIDE_EFFECT_LABELS[k] || k)}: <strong>{String(v)}</strong>
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <h3 className="text-xs font-semibold text-blue-900 mb-2">Handymen runtime (live)</h3>
          <dl className="text-[11px] space-y-1">
            {[
              ["company", data.handymen_runtime.company_name],
              ["industry", data.handymen_runtime.industry],
              ["greeting", data.handymen_runtime.greeting_message],
              ["proactive", data.handymen_runtime.proactive_message],
              ["tone", data.handymen_runtime.tone],
              ["scope basis", data.handymen_runtime.scope_basis],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="font-mono text-blue-700 w-20 shrink-0">{k}</dt>
                <dd className="text-blue-900">{v || <em className="opacity-50">(saknas)</em>}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <h3 className="text-xs font-semibold text-emerald-900 mb-2">Blueprint runtime</h3>
          <dl className="text-[11px] space-y-1">
            {[
              ["company", data.blueprint_runtime.company_name],
              ["industry", data.blueprint_runtime.industry],
              ["flow_type", data.blueprint_runtime.flow_type],
              ["assistant_role", data.blueprint_runtime.assistant_role],
              ["greeting", data.blueprint_runtime.greeting_message],
              ["proactive", data.blueprint_runtime.proactive_message],
              ["tone", data.blueprint_runtime.tone],
              ["scope basis", data.blueprint_runtime.scope_basis],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="font-mono text-emerald-700 w-24 shrink-0">{k}</dt>
                <dd className="text-emerald-900">{v || <em className="opacity-50">(saknas)</em>}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="rounded-lg border border-purple-200 bg-purple-50 p-3">
        <h3 className="text-xs font-semibold text-purple-900 mb-2">BUSINESS_SCOPE block (blueprint-runtime använder denna)</h3>
        <pre className="text-[11px] whitespace-pre-wrap font-mono text-purple-900" data-testid="business-scope-block">
{data.blueprint_runtime.business_scope_block}
        </pre>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
        <h3 className="text-xs font-semibold text-gray-900 flex items-center gap-2">
          <PlayCircle className="w-3.5 h-3.5" />
          Snabbt scope-test
        </h3>
        <p className="text-[11px] text-gray-500 -mt-1">
          Regelbaserad kontroll — inte ett AI-svar.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="Skriv testmeddelande..."
            data-testid="runtime-preview-test-message-input"
            className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded"
          />
          <button
            type="button"
            onClick={() => load(testMessage)}
            data-testid="runtime-preview-run-scope-test"
            className="px-3 py-1 text-xs bg-gray-900 text-white rounded"
          >
            Kör test
          </button>
        </div>
        {data.scope_test && (
          <div className={`rounded p-2 text-xs ${
            data.scope_test.verdict === "in_scope" ? "bg-emerald-50 text-emerald-900 border border-emerald-200" :
            data.scope_test.verdict === "out_of_scope" ? "bg-rose-50 text-rose-900 border border-rose-200" :
            "bg-amber-50 text-amber-900 border border-amber-200"
          }`} data-testid={`runtime-preview-scope-verdict-${data.scope_test.verdict}`}>
            <strong>Verdict:</strong> {data.scope_test.verdict}
            <div className="mt-1 opacity-90">{data.scope_test.reason}</div>
            {data.scope_test.out_of_scope_hits.length > 0 && (
              <div className="mt-1">Hits: {data.scope_test.out_of_scope_hits.join(", ")}</div>
            )}
            {data.scope_test.matched_terms.length > 0 && (
              <div className="mt-1">Matchande termer: {data.scope_test.matched_terms.join(", ")}</div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-emerald-200 bg-white p-3">
          <h3 className="text-xs font-semibold text-emerald-900 mb-2">Runtime-eligible actions ({data.actions.runtime_eligible.length})</h3>
          {data.actions.runtime_eligible.length === 0 ? (
            <div className="text-xs text-gray-500 italic">Inga.</div>
          ) : data.actions.runtime_eligible.map((a) => (
            <div key={a.action_id} className="text-xs py-1 border-b last:border-b-0 border-emerald-100" data-testid={`runtime-eligible-action-${a.action_id}`}>
              <span className="font-mono text-emerald-800">{a.action_id}</span>
              <span className="ml-2 text-gray-600">→ {a.label}</span>
              <span className="ml-2 text-[10px] font-mono text-gray-400">{a.url}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-amber-200 bg-white p-3">
          <h3 className="text-xs font-semibold text-amber-900 mb-2">Blockerade actions ({data.actions.blocked.length})</h3>
          {data.actions.blocked.length === 0 ? (
            <div className="text-xs text-gray-500 italic">Inga.</div>
          ) : data.actions.blocked.map((a) => (
            <div key={a.action_id} className="text-xs py-1 border-b last:border-b-0 border-amber-100">
              <span className="font-mono text-amber-800">{a.action_id}</span>
              <span className="ml-2 text-gray-600">— {a.blocked_reason?.join(", ")}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
          <h3 className="text-xs font-semibold text-rose-900 mb-1">Missing for blueprint runtime</h3>
          {data.missing_for_blueprint_runtime.length === 0 ? (
            <div className="text-xs text-emerald-700">Allt på plats!</div>
          ) : (
            <ul className="text-xs list-disc list-inside text-rose-900">
              {data.missing_for_blueprint_runtime.map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <h3 className="text-xs font-semibold text-amber-900 mb-1">Guards som skulle aktiveras</h3>
          <ul className="text-xs list-disc list-inside text-amber-900" data-testid="runtime-preview-guards">
            {data.guards.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─── Checkpoint H1: Shadow replay tab (admin-triggered, side-effect free) ─

const ASSESSMENT_LABEL = {
  blueprint_better: "Blueprint bättre",
  legacy_better: "Legacy bättre",
  equal: "Lika",
  needs_review: "Behöver granskning",
};

const ASSESSMENT_COLOR = {
  blueprint_better: "bg-emerald-100 text-emerald-800 border-emerald-300",
  legacy_better: "bg-amber-100 text-amber-800 border-amber-300",
  equal: "bg-slate-100 text-slate-700 border-slate-300",
  needs_review: "bg-blue-100 text-blue-800 border-blue-300",
};

const SCOPE_COLOR = {
  in_scope: "bg-emerald-100 text-emerald-800",
  out_of_scope: "bg-rose-100 text-rose-800",
  do_not_guess_match: "bg-amber-100 text-amber-800",
  uncertain: "bg-slate-100 text-slate-700",
};

function ShadowReplayTab({ clientId, getAuthHeaders }) {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/shadow-replay/history?limit=20`,
        { headers: getAuthHeaders(), credentials: "include" }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const payload = await r.json();
      setHistory(payload.runs || []);
    } catch {
      // history is optional; ignore on first load
    } finally {
      setLoadingHistory(false);
    }
  }, [clientId, getAuthHeaders]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const runReplay = async () => {
    if (!window.confirm(
      "Kör shadow replay nu? Det görs 10 LLM-anrop mot legacy (live-modell) "
      + "och upp till 10 anrop mot blueprint. INGA leads, INGA mejl, INGA "
      + "skrivningar till handymen/blueprints/presentations/actions sker. "
      + "Live widget påverkas inte. Endast 1 rad skrivs till shadow_replay_runs."
    )) return;
    setRunning(true);
    setReport(null);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/shadow-replay`,
        { method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({}) }
      );
      if (!r.ok) {
        const errText = await r.text();
        throw new Error(errText || `HTTP ${r.status}`);
      }
      const payload = await r.json();
      setReport(payload);
      await loadHistory();
      toast.success(`Replay klar — ${payload.summary.items_total} frågor körda`);
    } catch (e) {
      toast.error(`Replay misslyckades: ${e.message || e}`);
    } finally {
      setRunning(false);
    }
  };

  const openRun = async (runId) => {
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/shadow-replay/${runId}`,
        { headers: getAuthHeaders(), credentials: "include" }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const payload = await r.json();
      setReport(payload);
    } catch (e) {
      toast.error(`Kunde inte ladda rapport: ${e.message || e}`);
    }
  };

  return (
    <div className="space-y-4" data-testid="blueprint-shadow-replay-tab">
      <RuntimeStatusBanner
        clientId={clientId}
        testid="shadow-replay-runtime-banner"
        contextHint="Replay är admin-triggad och side-effect free — inga leads, mejl eller skrivningar utöver shadow_replay_runs."
      />

      <div className="rounded-lg border border-gray-200 bg-white p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <PlayCircle className="w-4 h-4" />
            Replay &amp; kvalitetstest
          </h2>
          <p className="text-xs text-gray-600 mt-1">
            Kör 10 förvalda frågor mot legacy + blueprint sida-vid-sida. Granska
            scope, actions, do_not_guess, lead capture och riskflaggor.
          </p>
        </div>
        <button
          onClick={runReplay}
          disabled={running}
          data-testid="shadow-replay-run-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Kör replay…</>
                   : <><PlayCircle className="w-4 h-4" /> Kör replay nu</>}
        </button>
      </div>

      {/* History */}
      {!report && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-xs font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <ListTree className="w-4 h-4" /> Tidigare replay-körningar
          </h3>
          {loadingHistory ? (
            <div className="flex items-center gap-2 text-gray-600 text-xs">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Laddar historik…
            </div>
          ) : history.length === 0 ? (
            <div className="text-xs text-gray-500 italic">
              Inga replay-körningar än. Klicka &quot;Kör replay nu&quot; för att starta.
            </div>
          ) : (
            <div className="space-y-1.5">
              {history.map((h) => (
                <button
                  key={h.id}
                  onClick={() => openRun(h.id)}
                  data-testid={`shadow-replay-history-${h.id}`}
                  className="w-full text-left px-3 py-2 rounded border border-gray-200 hover:bg-gray-50 flex items-center justify-between text-xs"
                >
                  <div>
                    <div className="font-mono text-gray-700">
                      {h.started_at?.replace("T", " ").slice(0, 19)}
                    </div>
                    <div className="text-gray-500">
                      Av {h.triggered_by} · suite={h.suite_id}
                    </div>
                  </div>
                  {h.summary && (
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                        Blueprint bättre: {h.summary.blueprint_better}
                      </span>
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                        Legacy bättre: {h.summary.legacy_better}
                      </span>
                      <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                        Granska: {h.summary.needs_review}
                      </span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Report */}
      {report && (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-sm font-semibold text-gray-900">
                Replay-rapport
              </h3>
              <button
                onClick={() => { setReport(null); }}
                className="text-xs text-gray-600 hover:text-gray-900 underline"
                data-testid="shadow-replay-back-to-history"
              >
                ← Tillbaka till historiken
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="rounded border p-2"><span className="text-gray-500">Items: </span><strong>{report.summary.items_total}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Blueprint bättre: </span><strong className="text-emerald-700">{report.summary.blueprint_better}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Legacy bättre: </span><strong className="text-amber-700">{report.summary.legacy_better}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Granska: </span><strong className="text-blue-700">{report.summary.needs_review}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Out-of-scope: </span><strong>{report.summary.out_of_scope_count}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">In-scope: </span><strong>{report.summary.in_scope_count}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Legacy LLM-anrop: </span><strong>{report.summary.total_legacy_llm_calls}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Blueprint LLM-anrop: </span><strong>{report.summary.total_blueprint_llm_calls}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Legacy total ms: </span><strong>{report.summary.total_legacy_latency_ms}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Blueprint total ms: </span><strong>{report.summary.total_blueprint_latency_ms}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Leads skapade: </span><strong className="text-emerald-700">{report.side_effects.leads_created}</strong></div>
              <div className="rounded border p-2"><span className="text-gray-500">Mejl skickade: </span><strong className="text-emerald-700">{report.side_effects.emails_sent}</strong></div>
            </div>
          </div>

          {report.items.map((it, i) => (
            <div key={it.question_id || i}
                 className="rounded-lg border border-gray-200 bg-white p-4 space-y-3"
                 data-testid={`shadow-replay-item-${it.question_id}`}>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs text-gray-500">Fråga {i + 1} · {it.category}</div>
                  <div className="text-sm font-semibold text-gray-900">{it.question}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${SCOPE_COLOR[it.scope_verdict] || "bg-gray-100 text-gray-700"}`}>
                    {it.scope_verdict}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs border ${ASSESSMENT_COLOR[it.assessment]}`}>
                    {ASSESSMENT_LABEL[it.assessment] || it.assessment}
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-600 italic">{it.scope_reason}</div>

              {it.scope_do_not_guess_rules?.length > 0 && (
                <div className="text-xs">
                  <span className="font-semibold text-amber-700">do_not_guess-träffar: </span>
                  {it.scope_do_not_guess_rules.map((r, idx) => (
                    <span key={idx} className="inline-block bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mr-1 mb-0.5">{r.rule}</span>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded border border-amber-200 bg-amber-50 p-3">
                  <div className="text-xs font-semibold text-amber-900 mb-1 flex items-center justify-between">
                    <span>Legacy answer (live kod)</span>
                    <span className="text-amber-700">{it.legacy_latency_ms} ms</span>
                  </div>
                  <div className="text-xs text-gray-800 whitespace-pre-wrap">{it.legacy_reply}</div>
                  <div className="mt-2 text-xs text-amber-800">
                    intent: <code>{it.legacy_intent}</code>
                    {it.legacy_lead_ready && <span className="ml-1 px-1.5 rounded bg-rose-100 text-rose-800">lead_ready</span>}
                  </div>
                  {it.legacy_risk_flags?.length > 0 && (
                    <div className="mt-1 text-xs text-rose-700">
                      flags: {it.legacy_risk_flags.join(", ")}
                    </div>
                  )}
                </div>
                <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
                  <div className="text-xs font-semibold text-emerald-900 mb-1 flex items-center justify-between">
                    <span>Blueprint preview answer</span>
                    <span className="text-emerald-700">{it.blueprint_latency_ms} ms</span>
                  </div>
                  <div className="text-xs text-gray-800 whitespace-pre-wrap">{it.blueprint_reply}</div>
                  <div className="mt-2 text-xs text-emerald-800">
                    intent: <code>{it.blueprint_intent}</code>
                    {it.blueprint_provider && <span className="ml-1 px-1.5 rounded bg-emerald-100 text-emerald-800">{it.blueprint_provider}/{it.blueprint_model}</span>}
                  </div>
                  {it.blueprint_risk_flags?.length > 0 && (
                    <div className="mt-1 text-xs text-rose-700">
                      flags: {it.blueprint_risk_flags.join(", ")}
                    </div>
                  )}
                </div>
              </div>

              {it.actions_blueprint_would_render?.length > 0 && (
                <div className="text-xs">
                  <span className="font-semibold text-gray-700">Actions blueprint skulle rendera: </span>
                  {it.actions_blueprint_would_render.map((a) => (
                    <span key={a.action_id}
                          className={`inline-block rounded border px-1.5 py-0.5 mr-1 mb-0.5 ${
                            a.emitted ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                                      : "bg-gray-50 border-gray-200 text-gray-500"}`}>
                      [{a.emitted ? "✓" : "—"}] {a.label}
                    </span>
                  ))}
                </div>
              )}

              {it.lead_ready_diff && (
                <div className="text-xs px-2 py-1 rounded bg-rose-50 text-rose-800 border border-rose-200">
                  lead_ready diff: legacy={String(it.legacy_lead_ready)}, blueprint={String(it.blueprint_lead_ready)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlueprintDetail({ clientId, onBack, getAuthHeaders }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [draftJson, setDraftJson] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const { status: runtimeStatus } = useRuntimeStatus(clientId);
  const isBlueprintActive = runtimeStatus?.active_runtime === "blueprint";
  const publishedVersion = runtimeStatus?.blueprint_version;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}`,
        { headers: getAuthHeaders(), credentials: "include" },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const payload = await r.json();
      setData(payload);
      setDraftJson(JSON.stringify(payload.blueprint?.client_config || {}, null, 2));
      setJsonError("");
    } catch {
      toast.error("Kunde inte ladda blueprint");
    } finally {
      setLoading(false);
    }
  }, [clientId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const completeness = data?.completeness;
  const blueprint = data?.blueprint;

  const fieldEntries = useMemo(() => {
    if (!completeness) return [];
    return [
      ...completeness.filled_fields.map((k) => ({ key: k, kind: "filled" })),
      ...completeness.missing_fields.map((k) => ({ key: k, kind: "missing" })),
    ];
  }, [completeness]);

  const handleSave = async () => {
    let parsed;
    try {
      parsed = JSON.parse(draftJson);
    } catch (e) {
      setJsonError(String(e.message || e));
      toast.error("Ogiltig JSON");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/client-config`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({ client_config: parsed }),
        },
      );
      if (!r.ok) {
        const body = await r.text();
        throw new Error(body || `HTTP ${r.status}`);
      }
      toast.success("Blueprint sparad");
      await load();
    } catch (err) {
      toast.error(`Kunde inte spara: ${err.message || err}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!completeness?.ready_to_publish) {
      toast.error(
        `Publicering blockerad — saknar: ${completeness?.publish_blockers?.join(", ") || "?"}`,
      );
      return;
    }
    if (!window.confirm(
      "Publicera blueprint?\n\n" +
      "För klienter där blueprint-runtime är aktiv (t.ex. elido_official) " +
      "börjar den nya versionen serveras direkt. För övriga klienter " +
      "blir blueprinten granskningsunderlag tills runtime_source växlas."
    )) return;
    setPublishing(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/publish`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: JSON.stringify({ confirm: true }),
        },
      );
      if (!r.ok) {
        const text = await r.text();
        throw new Error(text || `HTTP ${r.status}`);
      }
      toast.success("Blueprint publicerad (databasen)");
      await load();
    } catch (err) {
      toast.error(`Kunde inte publicera: ${err.message || err}`);
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm("Avpublicera blueprinten (arkivera)?")) return;
    setPublishing(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/unpublish`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          credentials: "include",
          body: "{}",
        },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Blueprint arkiverad");
      await load();
    } catch (err) {
      toast.error(`Kunde inte avpublicera: ${err.message || err}`);
    } finally {
      setPublishing(false);
    }
  };

  if (loading || !blueprint || !completeness) {
    return (
      <div className="flex items-center gap-2 text-gray-600 p-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Laddar blueprint…
      </div>
    );
  }

  const status = blueprint.status;
  const isPublished = status === "published";

  return (
    <div className="space-y-6" data-testid="blueprint-detail">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="blueprint-back"
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Tillbaka
        </button>
        <h1 className="text-lg font-semibold text-gray-900">
          {blueprint.client_config?.company_name || blueprint.client_id}
        </h1>
        <StatusBadge status={status} />
        <span className="text-xs text-gray-400">v{blueprint.version}</span>
        <span className="text-xs font-mono text-gray-500">{blueprint.client_id}</span>
        <span className="text-xs font-mono text-gray-300 truncate"
              title={blueprint.id}>
          {blueprint.id}
        </span>
      </div>

      {/* Runtime banner — dynamic per client */}
      <RuntimeStatusBanner clientId={clientId} testid="runtime-not-active-banner" />

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-gray-200" role="tablist">
        {[
          { id: "overview", label: "Översikt", icon: ListTree },
          { id: "diff",     label: "Legacy vs Blueprint", icon: GitCompareArrows },
          { id: "actions",  label: "Approved Actions", icon: MousePointerClick },
          { id: "runtime",  label: "Runtime jämförelse", icon: Zap },
          { id: "evidence", label: "Verifierade fakta", icon: ShieldCheck },
          { id: "catalog",  label: "Produktdata", icon: ShoppingBag },
          { id: "replay",   label: "Replay & kvalitetstest", icon: PlayCircle },
        ].map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              data-testid={`blueprint-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition ${
                active
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === "diff" && (
        <DiffTab clientId={clientId} getAuthHeaders={getAuthHeaders} />
      )}

      {activeTab === "actions" && (
        <ActionsPreviewTab clientId={clientId} getAuthHeaders={getAuthHeaders} />
      )}

      {activeTab === "runtime" && (
        <RuntimePreviewTab clientId={clientId} getAuthHeaders={getAuthHeaders} />
      )}

      {activeTab === "replay" && (
        <ShadowReplayTab clientId={clientId} getAuthHeaders={getAuthHeaders} />
      )}

      {activeTab === "evidence" && (
        <BlueprintEvidencePanel clientId={clientId} getAuthHeaders={getAuthHeaders} />
      )}

      {activeTab === "catalog" && (
        <CatalogPanel clientId={clientId} getAuthHeaders={getAuthHeaders} />
      )}

      {activeTab === "overview" && (<>
      {/* Completeness summary */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <ListTree className="w-4 h-4" />
            Fältöversikt — {completeness.filled_count}/{completeness.total_count} ifyllda
          </h2>
          <div className="flex items-center gap-2">
            {completeness.ready_to_publish ? (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                <ShieldCheck className="w-3.5 h-3.5" /> Redo att publicera
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-amber-700"
                    data-testid="publish-blocker-summary">
                <ShieldAlert className="w-3.5 h-3.5" />
                Blockerar publicering: {completeness.publish_blockers.join(", ")}
              </span>
            )}
          </div>
        </div>
        <FieldGrid entries={fieldEntries} />
        {completeness.publish_warnings?.length > 0 && (
          <p className="text-xs text-amber-700 mt-3 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Rekommenderade fält saknas (ej blocker):{" "}
            {completeness.publish_warnings.join(", ")}
          </p>
        )}
      </div>

      {/* JSON editor */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            Redigera blueprint (JSON)
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
              ändringar → review_required
            </span>
          </h2>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            data-testid="blueprint-save-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:bg-gray-300"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Spara som utkast för granskning
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Sparade JSON-ändringar blir <strong>review_required</strong> och
          påverkar inte runtime förrän de publiceras. Okända fält dödas
          tyst av backend-normaliseringen.
        </p>
        <textarea
          data-testid="blueprint-json-editor"
          rows={20}
          value={draftJson}
          onChange={(e) => { setDraftJson(e.target.value); setJsonError(""); }}
          spellCheck={false}
          className="w-full font-mono text-xs p-3 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
        {jsonError && (
          <p className="text-xs text-red-600">JSON-fel: {jsonError}</p>
        )}
      </div>

      {/* Publish actions */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3" data-testid="publish-section">
        <h2 className="text-sm font-semibold text-gray-900">Publicering</h2>
        {isBlueprintActive ? (
          <div className="text-xs text-gray-600 space-y-1.5">
            <p data-testid="publish-copy-blueprint">
              <strong>Blueprint-runtime är aktiv för denna klient.</strong>
              {publishedVersion ? <> Aktiv version: <span className="font-mono">v{publishedVersion}</span>.</> : null}
            </p>
            <p>
              Sparade JSON-ändringar blir <code>review_required</code> och
              påverkar <em>inte</em> chatten förrän de publiceras.
              När du publicerar en ny version börjar den serveras direkt
              till live-widgeten.
            </p>
            <p className="text-gray-500">
              Rollback finns alltid via <code>runtime_source = handymen</code>{" "}
              (växla i Runtime jämförelse-fliken).
            </p>
          </div>
        ) : (
          <div className="text-xs text-gray-600 space-y-1.5">
            <p data-testid="publish-copy-legacy">
              <strong>Klienten kör legacy-runtime.</strong>{" "}
              Publicerad blueprint är granskningsunderlag tills
              <code> runtime_source </code> växlas till <code>blueprint</code>{" "}
              och klienten läggs på allowlistan.
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={publishing || isPublished || !completeness.ready_to_publish}
            onClick={handlePublish}
            data-testid="blueprint-publish-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Publicera blueprint
          </button>
          {isPublished && (
            <button
              type="button"
              disabled={publishing}
              onClick={handleUnpublish}
              data-testid="blueprint-unpublish-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-md border border-gray-300 hover:bg-gray-200"
            >
              <Archive className="w-3.5 h-3.5" /> Avpublicera (arkivera)
            </button>
          )}
          {!completeness.ready_to_publish && (
            <span className="text-xs text-amber-700" data-testid="publish-disabled-reason">
              Knappen aktiveras när: {completeness.publish_blockers.join(", ")} fyllts i.
            </span>
          )}
        </div>
        {blueprint.published_at && (
          <p className="text-xs text-gray-500">
            Senast publicerad: {blueprint.published_at} av {blueprint.published_by}
          </p>
        )}
      </div>

      {/* Approved actions (read-only) */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          Client-approved actions
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="text-xs text-gray-400 inline-flex items-center gap-0.5"
            title="Read-only listing"
          >
            (read-only)
          </a>
        </h2>
        <ApprovedActionsList clientId={clientId} getAuthHeaders={getAuthHeaders} />
      </div>

      {/* Metadata footer */}
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Metadata</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-1.5 text-xs">
          <dt className="text-gray-500">blueprint_id</dt>
          <dd className="font-mono text-gray-800">{blueprint.id}</dd>
          <dt className="text-gray-500">approved_action_ids</dt>
          <dd className="font-mono text-gray-800">
            {(blueprint.approved_action_ids || []).join(", ") || "(tom)"}
          </dd>
          <dt className="text-gray-500">created_at / by</dt>
          <dd className="text-gray-800">{blueprint.created_at} / {blueprint.created_by}</dd>
          <dt className="text-gray-500">updated_at / by</dt>
          <dd className="text-gray-800">{blueprint.updated_at} / {blueprint.updated_by || "—"}</dd>
          <dt className="text-gray-500">source_evidence_ref</dt>
          <dd className="font-mono text-gray-800 break-all">
            {blueprint.source_evidence_ref || "—"}
          </dd>
          <dt className="text-gray-500">admin_notes</dt>
          <dd className="text-gray-800 whitespace-pre-wrap">{blueprint.admin_notes || "—"}</dd>
        </dl>
      </div>
      </>)}
    </div>
  );
}

// ─── Top-level component ────────────────────────────────────────────────

export default function BlueprintsAdmin() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  // H3.2.3 — when the route is `/admin/blueprints/:clientId` we
  // deep-jump straight into that client's detail view. The list is
  // bypassed entirely so admins coming from Kund 360 don't have to
  // re-find their client.
  const { clientId: clientIdFromUrl } = useParams();
  const [loading, setLoading] = useState(true);
  const [blueprints, setBlueprints] = useState([]);
  const [runtimeNotice, setRuntimeNotice] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(
    clientIdFromUrl || null
  );

  // Keep state in sync with the URL in BOTH directions (Admin Cleanup 0):
  // direct URL → detail; URL without clientId → list. Tidigare fastnade
  // tillbakaknappen eftersom state nollställdes men URL:en behöll kunden.
  useEffect(() => {
    setSelectedClientId(clientIdFromUrl || null);
  }, [clientIdFromUrl]);

  const openClient = useCallback((cid) => {
    navigate(`/admin/blueprints/${encodeURIComponent(cid)}`);
  }, [navigate]);

  const backToList = useCallback(() => {
    navigate("/admin/blueprints");
  }, [navigate]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/blueprints`, {
        headers: getAuthHeaders(),
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setBlueprints(data.blueprints || []);
      setRuntimeNotice(data.runtime_active_reason || "");
    } catch {
      toast.error("Kunde inte ladda blueprints");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (selectedClientId == null) loadList();
  }, [selectedClientId, loadList]);

  if (selectedClientId) {
    return (
      <div className="space-y-4">
        <BlueprintDetail
          clientId={selectedClientId}
          onBack={backToList}
          getAuthHeaders={getAuthHeaders}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="blueprints-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            Agent Blueprints
          </h1>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">
            Granska och redigera <code>agent_blueprints</code>. Blueprint-runtime
            är aktiv för utvalda klienter — övriga använder fortfarande{" "}
            <code>handymen</code>-runtime och kan granskas härifrån utan att
            chatten påverkas.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-2">
        <Info className="w-4 h-4 text-slate-600 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-800 leading-snug">
          Öppna en klient nedan för per-klient runtime-status, diff,
          Approved Actions och replay.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-600 p-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Laddar blueprints…
        </div>
      ) : (
        <BlueprintsList
          blueprints={blueprints}
          onSelect={openClient}
          runtimeNotice={runtimeNotice}
        />
      )}
    </div>
  );
}
